import React, { useEffect, useState } from "react";
import Sidebar from "./sidebar";
import axios from "../../utils/axios";
import toast, { Toaster } from "react-hot-toast";
import {
  trackAction,
  FEATURE_LABELS,
} from "../../utils/clickTracking";
import { getPropertyUpdateErrorMessage } from "../../utils/propertyErrors";
import DuplicatePt5Modal from "../../components/DuplicatePt5Modal";
import { useDuplicatePt5Retry } from "../../hooks/useDuplicatePt5Retry";
import { AnimatePresence, motion } from "framer-motion";

const Added = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [editPin, setEditPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProperty, setDeletingProperty] = useState(null);
  const [deleteComment, setDeleteComment] = useState("");

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [showNotReassessModal, setShowNotReassessModal] = useState(false);
  const [showRefModal, setShowRefModal] = useState(false);
  const [features, setFeatures] = useState([]);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notReassessComment, setNotReassessComment] = useState("");
  const [overallExplanation, setOverallExplanation] = useState("");
  const [initialAfterValues, setInitialAfterValues] = useState({});
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const {
    showDuplicatePt5Modal,
    openDuplicatePt5Modal,
    closeDuplicatePt5Modal,
    saveDuplicatePt5Retry,
  } = useDuplicatePt5Retry();

  const applyNewPt5 = (newPt5) => {
    setReferenceNumber(newPt5);
    setFeatures((prev) =>
      prev.map((f) => (f.key === "pt" ? { ...f, after: newPt5 } : f))
    );
  };

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const userId = user?.id;

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        const uid = userData?.id;

        if (!uid) {
          setError("User not found in localStorage");
          setLoading(false);
          return;
        }

        const response = await axios.post("/properties/added", { userId: uid });
        console.log("Fetched properties:", response.data.properties);
        if (response.data.properties.length > 0) {
          console.log("First property object:", response.data.properties[0]);
          console.log("First property keys:", Object.keys(response.data.properties[0]));
        }
        setProperties(response.data.properties);
      } catch (err) {
        console.error("API Error:", err);
        setError("Failed to fetch properties");
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await axios.post("/properties/added", { userId });
      setProperties(response.data.properties);
    } catch (err) {
      console.error("API Error:", err);
      setError("Failed to fetch properties");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setEditPin(property.pin);
  };

  const handleCancelEdit = () => {
    setEditingProperty(null);
    setEditPin("");
  };

  const handleSaveEdit = async () => {
    if (!editingProperty || !editPin) {
      toast.error("Please fill the PIN!");
      return;
    }

    setSubmitting(true);
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      const formData = new FormData();
      formData.append("pin", editPin);
      formData.append("circle", editingProperty.circle || "");
      formData.append("userId", userData?.id);

      await axios.put(`/properties/${editingProperty.pin}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Property updated successfully!");
      const response = await axios.post("/properties/added", { userId: userData?.id });
      setProperties(response.data.properties);
      setEditingProperty(null);
      setEditPin("");
    } catch (error) {
      console.error("Error updating property:", error);
      if (error.response?.data?.error) {
        if (error.response.data.duplicatePin) {
          toast.error(`PT-5 Serial Number already exists for PIN: ${error.response.data.duplicatePin}`);
        } else {
          toast.error(error.response.data.error);
        }
      } else {
        toast.error("Failed to update property.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post("/properties/bulk-add", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(`Successfully added ${response.data.successCount} properties!`);
      if (response.data.failedCount > 0) {
        toast.error(`Failed to add ${response.data.failedCount} properties.`);
      }

      const userData = JSON.parse(localStorage.getItem("user"));
      const refreshResponse = await axios.post("/properties/added", { userId: userData?.id });
      setProperties(refreshResponse.data.properties);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.error || "Failed to upload file.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = (property) => {
    setDeletingProperty(property);
    setDeleteComment("");
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteComment.trim()) {
      toast.error("Please provide a reason for deletion.");
      return;
    }

    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("comment", deleteComment.trim());
      fd.append("userId", userId);
      await axios.delete(`/properties/${deletingProperty.id}`, {
        data: fd,
      });
      const reasonText = deleteComment.trim();
      await trackAction("property_deleted", deletingProperty.pin, reasonText, {
        comment_text: reasonText,
      });
      toast.success("Property deleted successfully!");
      const userData = JSON.parse(localStorage.getItem("user"));
      const response = await axios.post("/properties/added", { userId: userData?.id });
      setProperties(response.data.properties);
      setShowDeleteModal(false);
      setDeletingProperty(null);
      setDeleteComment("");
    } catch (error) {
      console.error("Error deleting property:", error);
      toast.error(error.response?.data?.error || "Failed to delete property.");
    } finally {
      setSubmitting(false);
    }
  };

  const setFeaturesForReassess = (p) => {
    const newFeatures = [
      {
        key: "floor",
        label: "Floor",
        before: p.floor_admin || "",
        after: p.floors_pred ?? "",
        unit: "",
      },
      {
        key: "landuse",
        label: "Land Use",
        before: p.landuse_admin || "",
        after: p.land_use_pred ?? "",
        unit: "",
      },
      {
        key: "landarea",
        label: "Land Area",
        before: p.landarea_admin || "",
        after: p.landarea_combined_pred ?? "",
        unit: "sq.ft",
      },
      {
        key: "coveredarea",
        label: "Covered Area",
        before: p.coveredarea_admin || "",
        after: p.coveredarea_combined_pred ?? "",
        unit: "sq.ft",
      },
      {
        key: "pt",
        label: "PT-5 Serial Number",
        before: " ",
        after: p.refno || "",
        unit: "",
      },
    ];
    const initialAfter = {};
    newFeatures.forEach((f) => {
      initialAfter[f.key] = f.after;
    });
    setFeatures(newFeatures);
    setInitialAfterValues(initialAfter);
  };

  const updateFeature = (index, field, value) => {
    setFeatures((prev) => {
      const updated = [...prev];
      const f = updated[index];

      let validatedValue = value;
      if (field === "after" || field === "before") {
        switch (f.key) {
          case "floor":
          case "landarea":
          case "coveredarea":
            if (/[^0-9.]/.test(value)) {
              toast.error("Please enter only numbers for this field!");
              validatedValue = value.replace(/[^0-9.]/g, "");
            }
            if ((validatedValue.match(/\./g) || []).length > 1) {
              validatedValue = validatedValue.slice(0, -1);
            }
            if (validatedValue) {
              const numVal = parseFloat(validatedValue);
              if (f.key === "landarea") {
                if (numVal < 0 || numVal > 108000) {
                  toast.error("Land Area must be between 0 and 108,000 sq yards!");
                }
              } else if (f.key === "coveredarea") {
                if (numVal < 0 || numVal > 272000) {
                  toast.error("Covered Area must be between 0 and 272,000 sq ft!");
                }
              }
            }
            break;
          case "landuse":
            if (/[0-9]/.test(value)) {
              toast.error("Please enter only letters for Land Use!");
              validatedValue = value.replace(/[0-9]/g, "");
            }
            break;
        }
      }

      updated[index][field] = validatedValue;
      return updated;
    });
  };

  const handleNotReassess = async (refnoOverride = null) => {
    try {
      setSubmitting(true);

      const fd = new FormData();

      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "0");
      fd.append("comment", notReassessComment);

      // Use editable before values from features (not static selectedProperty)
      const beforeByKey = {};
      features.forEach(f => { beforeByKey[f.key] = f.before; });
      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", beforeByKey.floor || "");
      fd.append("landuse_admin", beforeByKey.landuse || "");
      fd.append("landarea_admin", beforeByKey.landarea || "");
      fd.append("coveredarea_admin", beforeByKey.coveredarea || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");

      // const refVal = refnoOverride || referenceNumber || selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "";
      const overrideVal = typeof refnoOverride === "string" ? refnoOverride : "";
      const refVal = overrideVal || referenceNumber || selectedProperty.refno || selectedProperty.pt5 || selectedProperty.pt_5 || selectedProperty.pt || "";
      if (refVal) {
        fd.append("refno", refVal);
      }

      await axios.put(`/properties/notfiltered-id/${selectedProperty.id}`, fd);

      const trimmed = String(notReassessComment ?? "").trim();
      if (trimmed && userId && selectedProperty?.id) {
        try {
          await axios.post("/inspector/save-explanation", {
            property_id: selectedProperty.id,
            user_id: userId,
            overall_explanation: trimmed,
            row_explanations: {
              floor: "",
              landuse: "",
              landarea: "",
              coveredarea: "",
            },
          });
        } catch (err) {
          console.error("Failed to save explanation:", err);
        }
      }

      await trackAction(
        "do_not_reassess",
        selectedProperty.pin,
        null,
        {
          comment_text: notReassessComment?.trim()
            ? `Reason for not reassessing: ${notReassessComment.trim()}`
            : "No reason provided",
        }
      );

      toast.success("Successfully marked as 'Do Not Reassess'");
      setNotReassessComment("");
      setShowNotReassessModal(false);
      fetchProperties();
    } catch (err) {
      console.error(err);
      if (
        openDuplicatePt5Modal(err, async (newPt5) => {
          applyNewPt5(newPt5);
          await handleNotReassess(newPt5);
        })
      ) {
        return;
      }
      toast.error(getPropertyUpdateErrorMessage(err, "Failed to update status"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReassess = async ({ featuresOverride, refnoOverride } = {}) => {
    const activeFeatures = featuresOverride ?? features;

    try {
      setSubmitting(true);

      const fd = new FormData();

      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "1");

      // Use editable before values from activeFeatures (not static selectedProperty)
      const beforeByKey = {};
      activeFeatures.forEach(f => { beforeByKey[f.key] = f.before; });
      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", beforeByKey.floor || "");
      fd.append("landuse_admin", beforeByKey.landuse || "");
      fd.append("landarea_admin", beforeByKey.landarea || "");
      fd.append("coveredarea_admin", beforeByKey.coveredarea || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");

      const featureChanges = [];

      activeFeatures.forEach((f) => {
        const val =
          f.key === "pt" && refnoOverride
            ? refnoOverride
            : f.after?.toString().trim();
        const initialVal = initialAfterValues[f.key]?.toString().trim();
        const isModified = val !== initialVal;
        if (isModified) {
          let displayName;
          switch (f.key) {
            case "floor":
              displayName = "Number of Floors";
              break;
            case "landuse":
              displayName = "Land Use";
              break;
            case "landarea":
              displayName = "Land Area";
              break;
            case "coveredarea":
              displayName = "Covered Area";
              break;
            case "pt":
              displayName = "PT-5 Serial Number";
              break;
            default:
              displayName = f.key;
              break;
          }
          featureChanges.push(
            `${displayName} changed from "${initialVal || "Not set"}" to "${val || "Not set"
            }"`
          );
        }

        if (!val) return;

        switch (f.key) {
          case "floor":
            fd.append("floors_pred", val);
            break;
          case "landuse":
            fd.append("land_use_pred", val);
            break;
          case "landarea":
            fd.append("landarea_combined_pred", val);
            break;
          case "coveredarea":
            fd.append("coveredarea_combined_pred", val);
            break;
          case "pt":
            fd.append("refno", val);
            break;
        }
      });

      if (selectedProperty.exp_garv) fd.append("exp_garv", selectedProperty.exp_garv);

      await axios.put(`/properties/notfiltered-id/${selectedProperty.id}`, fd);

      let floorExp = "";
      let landuseExp = "";
      let landareaExp = "";
      let coveredareaExp = "";

      activeFeatures.forEach((feature) => {
        if (feature.explanation) {
          switch (feature.key) {
            case "floor":
              floorExp = feature.explanation;
              break;
            case "landuse":
              landuseExp = feature.explanation;
              break;
            case "landarea":
              landareaExp = feature.explanation;
              break;
            case "coveredarea":
              coveredareaExp = feature.explanation;
              break;
          }
        }
      });

      if (overallExplanation) {
        await trackAction("Overall Explanation", selectedProperty.pin, null, {
          overall_explanation: overallExplanation,
        });
      }
      if (floorExp) {
        await trackAction("Number of Floors Explanation", selectedProperty.pin, null, {
          floor_explanation: floorExp,
        });
      }
      if (landuseExp) {
        await trackAction("Land Use Explanation", selectedProperty.pin, null, {
          landuse_explanation: landuseExp,
        });
      }
      if (landareaExp) {
        await trackAction("Land Area Explanation", selectedProperty.pin, null, {
          landarea_explanation: landareaExp,
        });
      }
      if (coveredareaExp) {
        await trackAction("Covered Area Explanation", selectedProperty.pin, null, {
          coveredarea_explanation: coveredareaExp,
        });
      }

      for (const feature of activeFeatures) {
        const oldVal = initialAfterValues[feature.key]?.toString().trim() || "";
        const newVal = feature.after?.toString().trim() || "";
        if (oldVal !== newVal) {
          const featureName = FEATURE_LABELS[feature.key] || feature.label || feature.key;
          await trackAction(`${feature.key}_updated`, selectedProperty.pin, null, {
            old_value: oldVal,
            new_value: newVal,
            comment_text: `${featureName} changed from "${oldVal}" to "${newVal}"`,
          });
        }
      }

      await trackAction("reassessed", selectedProperty.pin);
      toast.success("Reassessment saved successfully!");
      setShowReassessModal(false);
      setShowRefModal(false);
      fetchProperties();
    } catch (err) {
      console.error(err);
      if (
        openDuplicatePt5Modal(err, async (newPt5) => {
          const updatedFeatures = activeFeatures.map((f) =>
            f.key === "pt" ? { ...f, after: newPt5 } : f
          );
          applyNewPt5(newPt5);
          await handleSubmitReassess({
            featuresOverride: updatedFeatures,
            refnoOverride: newPt5,
          });
        })
      ) {
        return;
      }
      toast.error(getPropertyUpdateErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!readyToSubmit || submitting) return;
    handleSubmitReassess();
    setReadyToSubmit(false);
  }, [readyToSubmit, submitting]);

  const isReassessValid = features
    .filter((f) => f.key !== "pt")
    .some((f) => f.after && f.after.toString().trim() !== "");

  const handleOpenReassess = (property) => {
    setSelectedProperty(property);
    setFeaturesForReassess(property);
    setReferenceNumber(
      property.refno || property.pt5 || property.pt_5 || property.pt || ""
    );
    setShowReassessModal(true);
  };

  const handleOpenNotReassess = (property) => {
    setSelectedProperty(property);
    setNotReassessComment(property.inspector_overall_explanation || property.comment || "");
    setReferenceNumber(
      property.refno || property.pt5 || property.pt_5 || property.pt || ""
    );
    setShowNotReassessModal(true);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Sidebar className="w-64 bg-gray-900 text-white" />

      <div className="flex-1 p-10">
        <Toaster position="top-right" />
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Added By Me</h1>
          <div className="flex items-center gap-4">
            <label className="px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg shadow cursor-pointer hover:bg-blue-600 transition" data-click-id="added_by_me_upload_excel">
              {uploading ? "Uploading..." : "Upload Excel File"}
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {loading && <p>Loading...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {!loading && !error && (
          <div className="overflow-x-auto bg-white rounded-xl shadow">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="p-3 text-left">#</th>
                  <th className="p-3 text-left">PIN</th>
                  <th className="p-3 text-left">Circle</th>
                  <th className="p-3 text-left">Added At</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>

              <tbody>
                {properties.map((property, index) => (
                  <React.Fragment key={property.id}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-3 font-semibold">{index + 1}</td>
                      <td className="p-3 font-semibold">{property.pin}</td>
                      <td className="p-3">{property.circle || "-"}</td>
                      <td className="p-3">
                        {(() => {
                          const ts = property.created_at || property.safe_created_at;
                          console.log("Property PIN:", property.pin, "timestamp value:", ts);
                          if (!ts) return "-";
                          try {
                            return new Date(ts).toLocaleString("en-GB", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: false,
                            });
                          } catch (e) {
                            console.error("Error formatting date for PIN", property.pin, e);
                            return String(ts);
                          }
                        })()}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => handleEdit(property)}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                            data-click-id="added_by_me_edit"
                            data-property-id={property.pin}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(property)}
                            className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                            data-click-id="added_by_me_delete"
                            data-property-id={property.pin}
                          >
                            Delete
                          </button>
                          {/* <button
                            onClick={() => toggleRow(property.id)}
                            className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
                            data-click-id="added_by_me_toggle_details"
                            data-property-id={property.pin}
                          >
                            {expandedId === property.id ? "Hide" : "Details"}
                          </button> */}
                          <button
                            onClick={() => handleOpenNotReassess(property)}
                            disabled={submitting}
                            className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50"
                            data-click-id="added_by_me_do_not_reassess"
                            data-property-id={property.pin}
                          >
                            Do Not Reassess
                          </button>
                          <button
                            onClick={() => handleOpenReassess(property)}
                            disabled={submitting}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                            data-click-id="added_by_me_reassess"
                            data-property-id={property.pin}
                          >
                            Reassess
                          </button>
                        </div>
                      </td>
                    </tr>

                    {expandedId === property.id && (
                      <tr className="bg-gray-50">
                        <td colSpan="5" className="p-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <Row
                              label="Floors"
                              prev={property.floor_admin}
                              newVal={property.floors_pred}
                              ml={property.floors_ml}
                            />

                            <Row
                              label="Land Use"
                              prev={property.landuse_admin}
                              newVal={property.land_use_pred}
                              ml={property.landuse_ml}
                            />

                            <Row
                              label="Land Area"
                              prev={addUnit(property.landarea_admin, "sq. yards")}
                              newVal={addUnit(property.landarea_combined_pred, "sq. yards")}
                              ml={addUnit(property.landarea_ml, "sq. yards")}
                            />

                            <Row
                              label="Covered Area"
                              prev={addUnit(property.coveredarea_admin, "sq. ft")}
                              newVal={addUnit(property.coveredarea_combined_pred, "sq. ft")}
                              ml={addUnit(property.coveredarea_ml, "sq. ft")}
                            />

                            <Row
                              label="GARV"
                              prev={property.admin_garv}
                              newVal={property.exp_garv}
                            />

                            {property.refno && (
                              <Row label="Ref No" prev={property.refno} />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {properties.length === 0 && (
              <p className="p-4 text-gray-500">No properties found</p>
            )}
          </div>
        )}
      </div>

      {editingProperty && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Edit Property</h2>

            <div className="mb-4">
              <label className="block text-gray-600 font-medium mb-2">PIN Number</label>
              <input
                type="text"
                value={editPin}
                onChange={(e) => setEditPin(e.target.value)}
                placeholder="Enter PIN"
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleSaveEdit}
                disabled={!editPin || submitting}
                className={`flex-1 px-4 py-2 rounded text-white ${editPin && !submitting
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-gray-400 cursor-not-allowed"
                  }`}
                data-click-id="added_by_me_save_edit"
                data-property-id={editingProperty.pin}
              >
                {submitting ? "Saving..." : "Save"}
              </button>

              <button
                onClick={handleCancelEdit}
                className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                data-click-id="added_by_me_cancel_edit"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && deletingProperty && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl">
            <h2 className="text-xl font-bold mb-2 text-gray-800">Delete Property</h2>
            <p className="text-gray-600 text-sm mb-4">
              PIN: <span className="font-semibold">{deletingProperty.pin}</span>
            </p>

            <div className="mb-2">
              <label className="block text-gray-700 font-medium mb-2">
                Reason for deletion <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={4}
                value={deleteComment}
                onChange={(e) => setDeleteComment(e.target.value)}
                placeholder="Please explain why this property is being deleted..."
                className="w-full p-3 border rounded resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
                data-property-id={deletingProperty.pin}
              />
            </div>
            {!deleteComment.trim() && (
              <p className="text-red-500 text-sm mb-3">A deletion reason is required</p>
            )}

            <div className="flex gap-3 mt-4">
              <button
                onClick={confirmDelete}
                disabled={!deleteComment.trim() || submitting}
                className={`flex-1 px-4 py-2 rounded text-white ${deleteComment.trim() && !submitting
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-gray-400 cursor-not-allowed"
                  }`}
                data-click-id="added_by_me_confirm_delete"
                data-property-id={deletingProperty.pin}
              >
                {submitting ? "Deleting..." : "Confirm Delete"}
              </button>

              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingProperty(null);
                  setDeleteComment("");
                }}
                className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                data-click-id="added_by_me_cancel_delete"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showReassessModal && selectedProperty && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white p-6 rounded-xl w-[95vw] max-w-[1800px] shadow-xl max-h-[95vh] overflow-y-auto"
            >
              <h2 className="text-xl font-bold mb-4 text-gray-800">Reassessment</h2>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-3 text-left font-medium">Feature</th>
                      <th className="p-3 text-left font-medium">Current</th>
                      <th className="p-3 text-left font-medium">Updated</th>
                      <th className="p-3 text-left font-medium">Explanation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features
                      .filter((f) => f.key !== "pt")
                      .map((f, i) => {
                        const isAreaField = f.key === "landarea" || f.key === "coveredarea";
                        const unit = f.key === "landarea" ? "sq yds" : "sq ft";
                        return (
                          <tr key={i} className="border-b">
                            <td className="p-3 font-medium whitespace-nowrap">{f.label}</td>
                            <td className="p-3">
                              {f.key === "landuse" ? (
                                <select
                                  value={f.before || ""}
                                  onChange={(e) => updateFeature(i, "before", e.target.value)}
                                  className="w-full p-2 border rounded"
                                  disabled={submitting}
                                  data-property-id={selectedProperty?.pin}
                                >
                                  <option value="">Select Land Use</option>
                                  <option value="Residential">Residential</option>
                                  <option value="Mixed">Mixed</option>
                                  <option value="Commercial">Commercial</option>
                                </select>
                              ) : isAreaField ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    value={f.before || ""}
                                    onChange={(e) => updateFeature(i, "before", e.target.value)}
                                    className="flex-1 p-2 border rounded"
                                    placeholder={`Enter ${f.label.toLowerCase()}...`}
                                    data-property-id={selectedProperty?.pin}
                                  />
                                  <span className="text-gray-500 text-sm whitespace-nowrap">
                                    {unit}
                                  </span>
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  value={f.before || ""}
                                  onChange={(e) => updateFeature(i, "before", e.target.value)}
                                  className="w-full p-2 border rounded"
                                  placeholder={`Enter ${f.label.toLowerCase()}...`}
                                  data-property-id={selectedProperty?.pin}
                                />
                              )}
                            </td>
                            <td className="p-3">
                              {f.key === "landuse" ? (
                                <select
                                  value={f.after || ""}
                                  onChange={(e) => updateFeature(i, "after", e.target.value)}
                                  className="w-full p-2 border rounded"
                                  disabled={submitting}
                                  data-property-id={selectedProperty?.pin}
                                >
                                  <option value="">Select Land Use</option>
                                  <option value="Residential">Residential</option>
                                  <option value="Mixed">Mixed</option>
                                  <option value="Commercial">Commercial</option>
                                </select>
                              ) : isAreaField ? (
                                <div>&nbsp;</div>
                              ) : (
                                <input
                                  type="text"
                                  value={f.after || ""}
                                  onChange={(e) => updateFeature(i, "after", e.target.value)}
                                  className="w-full p-2 border rounded"
                                  placeholder={`Enter ${f.label.toLowerCase()}...`}
                                  data-property-id={selectedProperty?.pin}
                                />
                              )}
                            </td>
                            <td className="p-3">
                              {isAreaField ? (
                                <div>&nbsp;</div>
                              ) : (
                                <textarea
                                  rows={2}
                                  placeholder={`Explain ${f.label.toLowerCase()} change...`}
                                  value={f.explanation || ""}
                                  onChange={(e) => updateFeature(i, "explanation", e.target.value)}
                                  className="w-full p-2 border rounded text-sm text-gray-600 resize-none"
                                  data-property-id={selectedProperty?.pin}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Overall Explanation <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  rows={2}
                  placeholder="Enter overall explanation for this reassessment..."
                  value={overallExplanation}
                  onChange={(e) => setOverallExplanation(e.target.value)}
                  className="w-full p-2 border rounded text-sm text-gray-600 resize-none"
                  data-property-id={selectedProperty?.pin}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setReferenceNumber("");
                    setShowReassessModal(false);
                    setShowRefModal(true);
                  }}
                  disabled={!isReassessValid}
                  className={`flex-1 px-4 py-2 rounded text-white ${isReassessValid
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-blue-300 cursor-not-allowed"
                    }`}
                >
                  Next
                </button>

                <button
                  onClick={() => {
                    setShowReassessModal(false);
                    setOverallExplanation("");
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                  data-click-id="reassessment canceled"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <DuplicatePt5Modal
        isOpen={showDuplicatePt5Modal}
        onClose={closeDuplicatePt5Modal}
        onSave={saveDuplicatePt5Retry}
        submitting={submitting}
      />

      <AnimatePresence>
        {showRefModal && selectedProperty && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
            >
              <h2 className="text-xl font-bold mb-4 text-gray-800">
                {selectedProperty.refno ||
                  selectedProperty.pt5 ||
                  selectedProperty.pt_5 ||
                  selectedProperty.pt
                  ? "Edit PT-5 Serial Number"
                  : "Enter PT-5 Serial Number"}
              </h2>

              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="PT-5 Serial Number"
                className="w-full p-3 border rounded mb-2"
              />

              {!referenceNumber && (
                <p className="text-red-500 text-sm mb-3">Reference number is required</p>
              )}

              <div className="flex gap-3 mt-4">
                <button
                  onClick={async () => {
                    if (!referenceNumber) return;

                    const previousPt5 =
                      selectedProperty.refno ||
                      selectedProperty.pt5 ||
                      selectedProperty.pt_5 ||
                      selectedProperty.pt ||
                      "";

                    if (previousPt5 !== referenceNumber) {
                      await trackAction("pt5_save", selectedProperty.pin, null, {
                        featureChanges: [
                          `PT-5 Serial Number changed from "${previousPt5 || "Not set"}" to "${referenceNumber || "Not set"
                          }"`,
                        ],
                      });
                    } else {
                      await trackAction("pt5_save", selectedProperty.pin);
                    }

                    setFeatures((prev) =>
                      prev.map((f) =>
                        f.key === "pt" ? { ...f, after: referenceNumber } : f
                      )
                    );

                    setShowRefModal(false);
                    setReadyToSubmit(true);
                  }}
                  disabled={!referenceNumber || submitting}
                  className={`flex-1 px-4 py-2 rounded text-white ${referenceNumber
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                    }`}
                >
                  {submitting
                    ? "Saving..."
                    : selectedProperty.refno ||
                      selectedProperty.pt5 ||
                      selectedProperty.pt_5 ||
                      selectedProperty.pt
                      ? "Update"
                      : "Save"}
                </button>

                <button
                  onClick={() => setShowRefModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                  data-click-id="pt5_cancel"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showNotReassessModal && selectedProperty && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
            >
              <h2 className="text-xl font-bold mb-4 text-gray-800">
                Reason for Not Reassessing
              </h2>

              <div className="mb-4">
                <label className="block font-medium text-gray-700 mb-2">
                  {selectedProperty.refno ||
                    selectedProperty.pt5 ||
                    selectedProperty.pt_5 ||
                    selectedProperty.pt
                    ? "Edit PT-5 Serial Number"
                    : "PT-5 Serial Number"}
                </label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="PT-5 Serial Number"
                  className="w-full p-3 border rounded"
                />
              </div>

              <textarea
                value={notReassessComment}
                onChange={(e) => setNotReassessComment(e.target.value)}
                rows={4}
                placeholder="Enter reason / comments..."
                className="w-full p-3 border rounded mb-3"
              />

              {!notReassessComment && (
                <p className="text-red-500 text-sm mb-3">Comment is required</p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => handleNotReassess()}
                  disabled={!notReassessComment || submitting}
                  className={`flex-1 px-4 py-2 rounded text-white ${notReassessComment
                      ? "bg-gray-700 hover:bg-gray-800"
                      : "bg-gray-400 cursor-not-allowed"
                    }`}
                  data-click-id="reason_for_not_reassessing_confirm"
                  data-property-id={selectedProperty.pin}
                >
                  {submitting
                    ? "Saving..."
                    : selectedProperty.refno ||
                      selectedProperty.pt5 ||
                      selectedProperty.pt_5 ||
                      selectedProperty.pt
                      ? "Update"
                      : "Confirm"}
                </button>

                <button
                  onClick={() => setShowNotReassessModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                  data-click-id="reason_for_not_reassessing_cancel"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Row = ({ label, prev, newVal, ml }) => {
  if (!prev && !newVal && !ml) return null;

  return (
    <div className="border rounded-lg p-3 bg-white shadow-sm">
      <p className="text-sm font-semibold text-gray-600 mb-2">{label}</p>

      <div className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <span className="text-gray-400 text-xs">Previous</span>
          <p className="font-semibold">{prev || "-"}</p>
        </div>

        <div>
          <span className="text-gray-400 text-xs">New</span>
          <p className="font-semibold text-blue-600">{newVal || "-"}</p>
        </div>

        <div>
          <span className="text-gray-400 text-xs">Value</span>
          <p className="font-semibold text-green-600">{ml || "-"}</p>
        </div>
      </div>
    </div>
  );
};

const addUnit = (value, unit) => {
  if (!value) return "";
  const n = Number(String(value).replaceAll(",", "").trim());
  const formatted = Number.isFinite(n) && !Number.isNaN(n) ? String(Math.round(n)) : value;
  return `${formatted} ${unit}`;
};

export default Added;
